import styled, { css } from 'styled-components';
import { Text } from '~/components/ui/Text';

const StyledMainContainer = styled.div`
	display: grid;
	grid-template-columns: 390px 1px 1fr;
	height: 100%;
`;

const StyledPhotoContainer = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 260px;
	background-color: rgba(0, 0, 0, 0.25);

	img {
		user-select: none;
		pointer-events: none;
	}
`;

const StyledInfoHintContainer = styled.div<{ $position: 'left' | 'right' }>`
	position: absolute;
	top: 20px;
	${(props) =>
		props.$position === 'left' &&
		css`
			left: 20px;
		`}
	${(props) =>
		props.$position === 'right' &&
		css`
			right: 20px;
		`}
`;

const StyledInfoHint = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	gap: 4px;
	height: 20px;
	padding: 0 5px;
	background-color: #ffffff;
	border-radius: 6px;
	transition: background-color 0.3s ease;

	img {
		width: 10px;
		height: 10px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	${Text} {
		font-weight: 700;
		font-size: 12px;
		color: #000000;
		transition: color 0.3s ease;
	}
`;

const StyledPhotoThumbsContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
`;

const StyledPhotoThumb = styled.div<{
	$active?: boolean;
	$stock?: boolean;
	$type?: 'car' | 'house';
}>`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 80px;
	box-sizing: border-box;
	cursor: pointer;

	&::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		top: 0;
		bottom: 0;
		border: 3px solid transparent;
		transition: border 0.3s ease;
	}

	img {
		width: 100%;
		height: 100%;
		${(props) =>
			props.$type !== 'car' &&
			css`
				object-fit: cover;
			`}
		transition: opacity 0.3s ease;
		user-select: none;
		pointer-events: none;
	}

	${(props) =>
		props.$stock &&
		css`
			background-color: rgba(255, 255, 255, 0.1);
		`}

	${(props) =>
		props.$active &&
		css`
			&::after {
				border: 3px solid #2d70da;
			}
		`}
	
	${(props) =>
		!props.$stock &&
		css`
			img {
				opacity: 0.25;
			}

			&:hover {
				img {
					opacity: 1;
				}
			}

			${props.$active &&
			css`
				img {
					opacity: 1;
				}
			`}
		`}
`;

const StyledDescriptionContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 17px;
	padding: 18px 20px 23px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);

	.title {
		display: flex;
		align-items: center;
		gap: 10px;

		img {
			width: 14px;
			height: 10px;
			user-select: none;
			pointer-events: none;
		}

		${Text} {
			font-size: 14px;
			color: #ffffff;
			opacity: 0.75;
		}
	}

	& > ${Text} {
		display: -webkit-box;
		-webkit-line-clamp: 5;
		-webkit-box-orient: vertical;
		overflow: hidden;
		font-size: 14px;
		color: #ffffff;
		opacity: 0.5;
	}
`;

const StyledStatsContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px 0;

	${Text} {
		font-size: 14px;
		color: #ffffff;
		opacity: 0.25;
	}
`;

const StyledButtonContainer = styled.div`
	display: flex;
	padding: 20px;
	align-items: center;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const StyledCenterButtons = styled.div`
	display: grid;
	grid-template-columns: 180px 180px;
	gap: 10px;
	margin-left: 39px;
`;

const StyledButton = styled.div<{ $color: string; $disabled?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	height: 40px;
	border-radius: 10px;
	background-color: ${(props) =>
		props.$disabled ? 'rgba(255, 255, 255, 0.05)' : props.$color};
	transition: background-color 0.3s ease;

	${(props) =>
		!props.$disabled &&
		css`
			cursor: pointer;

			&:hover {
				background-color: #ffffff;

				${Text} {
					color: #000000;
				}

				img {
					filter: invert(100%);
				}
			}
		`}

	${Text} {
		font-size: 14px;
		color: ${(props) =>
			props.$disabled ? 'rgba(255,255,255,0.05)' : '#ffffff'};
		transition: color 0.3s ease;
	}

	img {
		width: 14px;
		height: 14px;
		user-select: none;
		pointer-events: none;
		opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
		transition:
			opacity 0.3s ease,
			filter 0.3s ease;
	}
`;

const StyledAboutTitle = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: 20px;
	padding: 0 20px;

	img {
		width: 14px;
		height: 10px;
		user-select: none;
		pointer-events: none;
	}

	${Text} {
		font-size: 14px;
		color: #ffffff;
		opacity: 0.75;
	}
`;

const StyledTableContainer = styled.div`
	display: grid;
	grid-template-columns: auto auto;
	row-gap: 14px;
	justify-content: space-between;
	margin: 20px;
	padding: 20px;
	background-color: rgba(255, 255, 255, 0.02);
	border-radius: 10px;

	${Text} {
		font-size: 14px;
		color: #ffffff;

		&:nth-child(2n) {
			text-align: right;
		}

		&:nth-child(2n + 1) {
			opacity: 0.25;
		}

		&.buy {
			color: #1f8034;
			cursor: pointer;
			transition: color 0.3s ease;

			&:hover {
				color: #ffffff;
			}
		}
	}
`;

const StyledBottomContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	padding: 0 20px;
`;

const StyledDivider = styled.div`
	width: 1px;
	height: 100%;
	background-color: rgba(255, 255, 255, 0.1);
`;

const StyledButtonContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
`;

export {
	StyledAboutTitle,
	StyledBottomContainer,
	StyledButton,
	StyledButtonContainer,
	StyledButtonContent,
	StyledCenterButtons,
	StyledDescriptionContainer,
	StyledDivider,
	StyledInfoHint,
	StyledInfoHintContainer,
	StyledMainContainer,
	StyledPhotoContainer,
	StyledPhotoThumb,
	StyledPhotoThumbsContainer,
	StyledStatsContainer,
	StyledTableContainer,
};
